import { useQuery, useLazyQuery } from "@apollo/client";
import {
  AsoebiStatusEnum,
  GetAseobiQuery,
  GetAseobiQueryVariables,
  GetAseobiAttiresQuery,
  GetAseobiAttiresQueryVariables,
} from "@src/@types/api.d";
import { GetAsoEbi, GetAsoEbiAttires } from "@src/graphql/queries";
import React, { useState } from "react";
import toast, { LoaderIcon } from "react-hot-toast";
import AsoebiImages from "./AsoebiImages";
import styles from "./styles.module.scss";
import AsoebiStep2 from "./AsoebiStep2";
import PaymentSuccess from "./PaymentSuccess";
import getSymbolFromCurrency from "currency-symbol-map";
import AsoebiPayment from "./AsoebiPayment";

import PackageShop, { GuestPackageOrders } from "./PackageShop";
import { GetPackages } from "@src/graphql/queries/packages.queries";

// const PAYMENT_SUCCESS_KEY = "asoebi-payment-success";

interface Props {
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AsoebiModal = ({ eventUuid, setOpen }: Props) => {
  const [selectedAsoebiId, setSelectedAsoebiId] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [packageShopping, setPackageShopping] = useState(false);
  const [packageHistory, setPackageHistory] = useState(false);
  // Track quantities for each attire item
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

  const [attires, setAttires] = useState<
    GetAseobiAttiresQuery["getAseobiAttires"]
  >([]);

  const { data: asoebi, loading: asoebiLoading } = useQuery<
    GetAseobiQuery,
    GetAseobiQueryVariables
  >(GetAsoEbi, {
    variables: { event_id: eventUuid ?? "" },
    fetchPolicy: "cache-and-network",
    onError(error) {
      toast.error(
        <p className="font-inter">{error?.message ?? "An error occurred"}</p>
      );
    },
  });
  const packageGroup = asoebi?.getAseobi?.find(
    (group) => group.id === selectedAsoebiId
  );
  const { data: packageData } = useQuery(GetPackages, {
    variables: { eventId: eventUuid ?? "", groupId: selectedAsoebiId ?? "" },
    skip: !eventUuid || !selectedAsoebiId || step !== 2,
    fetchPolicy: "cache-and-network",
  });
  const hasPackages = packageData?.getAseobiPackages?.some(
    (item) => item.status === "PUBLISHED"
  );

  const [
    getAsoebiAttires,
    { data: asoebiAttires, loading: asoebiAttiresLoading },
  ] = useLazyQuery<GetAseobiAttiresQuery, GetAseobiAttiresQueryVariables>(
    GetAsoEbiAttires,
    {
      fetchPolicy: "cache-and-network",
      onCompleted(data) {
        setAttires(
          (data.getAseobiAttires ?? []).filter(
            (item) => item.vendor === "COUPLE"
          )
        );
      },
      onError(error) {
        toast.error(
          <p className="font-inter">
            {error?.message ?? "Failed to load attires"}
          </p>
        );
      },
    }
  );

  // Handle Order button click
  const handleOrder = (id: string) => {
    setSelectedAsoebiId(id);
    setQuantities({}); // Reset quantities when selecting a new Aso-ebi
    setStep(2);

    // Call the lazy query to fetch attires for the selected asoebi
    if (eventUuid) {
      getAsoebiAttires({
        variables: { event_id: eventUuid, aseobi_id: id },
      });
    }
  };

  const updateQuantity = (attireId: string, delta: number) => {
    setQuantities((prev) => ({
      ...prev,
      [attireId]: Math.max(0, (prev[attireId] || 0) + delta),
    }));
  };
  const totalQuantity = (attires ?? []).reduce(
    (sum, item) => sum + (quantities[item.id] || 0),
    0
  );
  const totalPrice = (attires ?? []).reduce(
    (sum, item) => sum + item.total_price_per_yard * (quantities[item.id] || 0),
    0
  );

  const activeAsoebi = asoebi?.getAseobi?.filter(
    (item) => item.status === AsoebiStatusEnum.AsoebiStatusActive
  );

  if (packageHistory && eventUuid)
    return (
      <div className="bg-white max-h-[90vh] w-full xs:w-[564px] px-8 py-8 rounded-md overflow-y-auto thin-scrollbar">
        <button
          className="mb-5 text-sm font-inter text-[#3B5F59] underline"
          onClick={() => setPackageHistory(false)}
        >
          ← Back to asoebi
        </button>
        <GuestPackageOrders eventId={eventUuid} />
      </div>
    );

  if (packageShopping && packageGroup && eventUuid)
    return (
      <div className="bg-white max-h-[90vh] w-full xs:w-[564px] px-8 py-8 rounded-md overflow-y-auto thin-scrollbar">
        <PackageShop
          eventId={eventUuid}
          groupId={packageGroup.id}
          groupName={packageGroup.name}
          currency={packageGroup.currency_iso ?? "ngn"}
          deadline={packageGroup.deadline}
          onBack={() => setPackageShopping(false)}
        />
      </div>
    );

  return (
    <div className="bg-white max-h-[90vh] w-full xs:w-[564px] xs:min-h-[500px] px-8 py-11 xs:p-8 rounded-md flex flex-col gap-4 overflow-y-auto thin-scrollbar">
      {step === 1 ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <h2 className="mb-4 font-inter text-2xl font-semibold">Aso-ebi</h2>
            <button
              type="button"
              className="mb-4 text-sm font-inter text-[#3B5F59] underline"
              onClick={() => setPackageHistory(true)}
            >
              My orders
            </button>
          </div>
          {asoebiLoading ? (
            <div className="flex justify-center items-center flex-grow">
              <LoaderIcon />
            </div>
          ) : activeAsoebi?.length && activeAsoebi.length > 0 ? (
            activeAsoebi?.map((item) => (
              <div key={item.id} className="w-full max-w-[500px] font-inter">
                <div className="border p-4 flex justify-between items-center">
                  <div className="max-w-[70%]">
                    <p className="mb-2 font-inter">{item.name}</p>
                    <AsoebiImages asoebi_id={item.id} eventUuid={eventUuid} />
                  </div>
                  <button
                    className="border py-1 px-4 text-sm font-inter hover:bg-gray-100 transition duration-300"
                    onClick={() => handleOrder(item.id)}
                  >
                    Order
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center flex-grow">
              <p className="font-inter">Nothing to show here</p>
            </div>
          )}
        </>
      ) : step === 2 ? (
        <div className="flex flex-col flex-grow">
          <div className="mb-4">
            <h2 className="mb-4 font-inter text-2xl font-semibold">
              {
                asoebi?.getAseobi?.find((item) => item.id === selectedAsoebiId)
                  ?.name
              }
            </h2>
            <p className="text-sm text-gray-500 font-inter">
              Select your preferred quantity and make a one-time payment
            </p>
          </div>

          {packageGroup && hasPackages && (
            <div className="mb-5 rounded-xl border border-[#d8e5e2] bg-[#f0f5f4] p-4 font-inter">
              <h3 className="font-semibold text-[#3B5F59]">
                Shop a complete look
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Explore packages with fabric and matching accessories, sold
                together.
              </p>
              <button
                type="button"
                className="mt-3 rounded-lg bg-[#4e7e77] px-4 py-2 text-sm text-white"
                onClick={() => setPackageShopping(true)}
              >
                Browse packages
              </button>
            </div>
          )}
          {asoebiAttiresLoading ? (
            <div className="flex justify-center items-center flex-grow">
              <LoaderIcon />
            </div>
          ) : (
            <>
              <div
                className={`mb-8 xs:h-[350px] overflow-auto ${styles["custom-scroll"]}`}
              >
                {attires && attires.length > 0 ? (
                  attires.map((item) => (
                    <AsoebiStep2
                      key={item.id}
                      item={item}
                      quantity={quantities[item.id] || 0}
                      updateQuantity={(delta: number) =>
                        updateQuantity(item.id, delta)
                      }
                    />
                  ))
                ) : (
                  <div className="flex justify-center items-center flex-grow h-full">
                    <p className="font-inter">
                      There are no attires in this group
                    </p>
                  </div>
                )}
              </div>
              <div className="mb-4 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="font-inter font-medium">
                    Sub-total ({totalQuantity} items)
                  </span>
                  <span className="font-inter font-semibold text-lg">
                    {attires?.[0]?.currency_iso
                      ? getSymbolFromCurrency(attires[0].currency_iso)
                      : "NGN"}
                    {totalPrice.toLocaleString()}
                  </span>
                </div>

                {/* <div className="flex items-center justify-between">
                  <span className="font-inter text-sm">{`${asoebiFeePercentage}% Inawo service fee`}</span>
                  <span className="font-inter">
                    {attires[0]?.currency_iso
                      ? getSymbolFromCurrency(attires[0].currency_iso)
                      : "NGN"}
                    {formatMoney((asoebiFeePercentage / 100) * totalPrice)}
                  </span>
                </div> */}

                {/* <div className="flex items-center justify-between">
                  <span className="font-inter text-lg">Total</span>
                  <span className="font-inter text-lg font-semibold">
                    {attires[0]?.currency_iso
                      ? getSymbolFromCurrency(attires[0].currency_iso)
                      : "NGN"}
                    {formatMoney(totalPrice)}
                  </span>
                </div> */}
              </div>

              <div className="flex gap-2">
                <button
                  className="w-full py-2 border border-gray-500 hover:bg-gray-200 text-black font-inter rounded-md transition duration-300"
                  onClick={() => setStep(1)}
                >
                  Go back
                </button>
                <button
                  className={`${
                    totalQuantity === 0
                      ? `cursor-not-allowed bg-[#cccccc] border border-[#cccccc]`
                      : `hover:bg-[#34544F] bg-[#4e7e77]`
                  } w-full py-2 text-white font-inter rounded-md transition duration-300`}
                  onClick={() => setStep(6)}
                  disabled={totalQuantity === 0}
                >
                  Proceed
                </button>
              </div>
            </>
          )}
        </div>
      ) : step === 6 ? (
        <AsoebiPayment
          asoebi={asoebi}
          selectedAsoebiId={selectedAsoebiId}
          attires={attires}
          quantities={quantities}
          finalTotal={totalPrice}
          totalQuantity={totalQuantity}
          setStep={setStep}
          eventUuid={eventUuid}
          setOpen={setOpen}
        />
      ) : step === 7 ? (
        <PaymentSuccess />
      ) : null}
    </div>
  );
};

export default AsoebiModal;
